import { Link } from 'react-router-dom';
import { Clock, Eye } from 'lucide-react';
import { NewsArticle } from '@/hooks/useNews';
import { cn } from '@/lib/utils';

interface NewsCardProps {
  article: NewsArticle;
  variant?: 'default' | 'compact';
}

export function NewsCard({ article, variant = 'default' }: NewsCardProps) {
  const timeAgo = getTimeAgo(new Date(article.published_at));

  if (variant === 'compact') {
    return (
      <Link 
        to={`/news/${article.slug}`}
        className="group flex gap-3 p-3 rounded-xl bg-card border hover:border-primary/50 transition-all card-hover"
      >
        {article.image_url && (
          <div className="relative h-20 w-20 flex-shrink-0 rounded-lg overflow-hidden">
            <img 
              src={article.image_url} 
              alt={article.title}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <span>{article.source_name}</span>
            <span>•</span>
            <span>{timeAgo}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link 
      to={`/news/${article.slug}`}
      className="group flex flex-col rounded-xl bg-card border overflow-hidden hover:border-primary/50 transition-all card-hover"
    >
      <div className="relative h-48 overflow-hidden">
        {article.image_url ? (
          <img 
            src={article.image_url} 
            alt={article.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20" />
        )}
        <div className="absolute top-3 left-3">
          {article.category && (
            <span className="px-2 py-1 text-xs font-medium bg-background/90 backdrop-blur-sm rounded-full">
              {article.category.name}
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 p-4">
        <h3 className="font-display text-lg font-semibold line-clamp-2 mb-2 group-hover:text-primary transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {article.summary}
        </p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {article.source_logo && (
              <img 
                src={article.source_logo} 
                alt={article.source_name}
                loading="lazy"
                decoding="async"
                className="h-4 w-4 rounded"
              />
            )}
            <span>{article.source_name}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {timeAgo}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {article.view_count}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
